import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db934vrk13ra8mig90r0';

const guides = [
  {
    icon: 'ri-compass-3-line',
    title: '快速浏览',
    desc: '通过顶部导航进入电视剧、电影、综艺频道，使用题材、地区、年份等筛选快速定位想看的内容。',
    tone: 'primary',
  },
  {
    icon: 'ri-search-2-line',
    title: '精准搜索',
    desc: '在搜索框输入影视名称、主演或题材，即可从全站演示数据中找到匹配的影视作品。',
    tone: 'accent',
  },
  {
    icon: 'ri-bookmark-3-line',
    title: '收藏与分享',
    desc: '在详情页可点击收藏与分享按钮进行体验，相关功能为前端演示，不会持久化保存数据。',
    tone: 'secondary',
  },
];

const stats = [
  { value: '45+', label: '演示影视内容' },
  { value: '3', label: '核心频道分类' },
  { value: '4', label: '筛选维度' },
  { value: '100%', label: '本地演示数据' },
];

const toneClass: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-800',
  secondary: 'bg-secondary-100 text-secondary-800',
};

export default function About() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useSeo({
    title: '关于我们 - 牛牛视频',
    description:
      '牛牛视频是面向影视爱好者的在线影视内容聚合平台，提供热门电视剧、电影、综艺的分类浏览与推荐服务，本页介绍平台定位、服务范围与使用指南。',
    keywords: '牛牛视频,关于我们,影视平台介绍,使用指南',
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get('contact_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('contact_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => body.append(key, String(value)));

      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '提交失败，请稍后重试',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后重试');
    }
  };

  return (
    <div className="w-full pt-16 md:pt-20">
      {/* 头部 */}
      <div className="w-full bg-primary-500">
        <div className="w-full px-4 md:px-8 py-10 md:py-16">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-primary-200">
            <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">关于我们</span>
          </nav>
          <h1 className="mt-3 text-2xl md:text-4xl font-black text-background-50">关于牛牛视频</h1>
          <p className="mt-3 max-w-3xl text-sm md:text-base text-primary-100 leading-relaxed">
            牛牛视频是一个面向广大影视爱好者的在线影视内容聚合与推荐平台。我们致力于帮助用户快速浏览、筛选和发现热门及最新的影视作品，用清晰的分类和丰富的资讯，让每一次浏览都更高效。
          </p>
        </div>
      </div>

      {/* 数据概览 */}
      <section className="w-full px-4 md:px-8 -mt-8 md:-mt-10 relative z-10">
        <div className="rounded-lg overflow-hidden border border-background-200/70 grid grid-cols-2 md:grid-cols-4 gap-px bg-background-200/70">
          {stats.map((s) => (
            <div key={s.label} className="bg-background-50 p-5 md:p-6 text-center">
              <p className="text-2xl md:text-3xl font-black text-primary-600">{s.value}</p>
              <p className="mt-1 text-xs md:text-sm text-foreground-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 平台定位与服务范围 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-11 h-11 rounded-lg bg-primary-500 flex items-center justify-center shrink-0">
                <i className="ri-focus-3-line text-xl text-primary-50" />
              </span>
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">平台定位</h2>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-loose">
              牛牛视频专注于影视内容的聚合与展示，主要用户为爱看电视剧、电影和综艺的普通观众。我们的核心目标是让用户能够方便地浏览多类别影视作品，查看详细介绍，并获得相关推荐，提升整体浏览体验。
            </p>
          </div>

          <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-11 h-11 rounded-lg bg-accent-500 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-xl text-accent-950" />
              </span>
              <h2 className="text-lg md:text-xl font-bold text-foreground-950">服务范围说明</h2>
            </div>
            <ul className="space-y-2.5 text-sm md:text-base text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-check-line text-primary-500 mt-1" />
                提供电视剧、电影、综艺的分类浏览与检索
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line text-primary-500 mt-1" />
                提供影视简介、元信息与同类相关推荐
              </li>
              <li className="flex gap-2">
                <i className="ri-close-line text-foreground-400 mt-1" />
                不提供实际视频播放、下载或会员服务
              </li>
              <li className="flex gap-2">
                <i className="ri-close-line text-foreground-400 mt-1" />
                站内所有影视资料均为演示内容
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* 使用指南 */}
      <section className="w-full bg-background-100 border-y border-background-200/70">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-secondary-500" />
            <h2 className="text-lg md:text-2xl font-bold text-foreground-950">使用指南</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {guides.map((g) => (
              <div key={g.title} className="p-6 rounded-lg bg-background-50 border border-background-200/70">
                <span
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${toneClass[g.tone]}`}
                >
                  <i className={`${g.icon} text-2xl`} />
                </span>
                <h3 className="mt-4 text-base md:text-lg font-bold text-foreground-950">{g.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 反馈表单 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              <h2 className="text-lg md:text-2xl font-bold text-foreground-950">意见反馈</h2>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-loose">
              如果你对牛牛视频有任何建议、内容纠错或合作想法，欢迎通过右侧表单告诉我们。
            </p>
            <div className="mt-5 p-4 rounded-lg bg-accent-50 border border-accent-200 text-sm text-accent-900 leading-relaxed">
              <i className="ri-information-line mr-1" />
              本站暂未开通人工客服，反馈提交后我们会在能力范围内尽快处理。请勿提交敏感个人信息。
            </div>
            <ul className="mt-5 space-y-3 text-sm text-foreground-600">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500" />
                邮箱：contact@niuniu-video.com
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500" />
                服务时间：工作日 09:00 - 18:00
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <form
              id="feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    称呼 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的称呼"
                    className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fb-topic" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  反馈类型
                </label>
                <select
                  id="fb-topic"
                  name="topic"
                  defaultValue="内容建议"
                  className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="内容纠错">内容纠错</option>
                  <option value="功能体验">功能体验</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  反馈内容 <span className="text-accent-600">*</span>
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的建议或问题（最多 500 字）"
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                />
              </div>

              {/* 反垃圾蜜罐字段 */}
              <div className="form-alt-field-wrap" aria-hidden="true">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="h-11 px-7 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-bold whitespace-nowrap transition-colors cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>
                {status === 'success' && (
                  <span className="text-sm text-primary-600 flex items-center gap-1.5">
                    <i className="ri-checkbox-circle-line" />
                    提交成功，感谢你的反馈！
                  </span>
                )}
                {status === 'error' && (
                  <span className="text-sm text-accent-700 flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />
                    {formError}
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}